import {
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps
} from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  reducerUpdateAnalyticArgumentDateRange,
  reducerUpdateAnalyticArgumentDateRangeOptions
} from 'src/features/analyticArguments';
import { reducerUpdateComparisonDateRange } from 'src/features/sosmedComparison';
import { reducerUpdateOfmComparisonDateRange } from 'src/features/ofmComparison';
import { reducerUpdateOnmComparisonDateRange } from 'src/features/onmComparison';
import ModalCustomTimeFrame from 'src/components/ModalCustomTimeFrame';
import { IDateRange, IOptionDateRange } from 'src/models/general';
import { reducerUpdateStatisticGranularity } from 'src/features/onlineMediaStream';
import { getGranularityForChronological } from 'src/utils/getGranularityForChronological';
import { useAlert } from 'src/hooks/useAlert';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import { t } from 'i18next';

let now = new Date();
let start = moment(
  new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
);
let end = moment(start).add(1, 'days').subtract(1, 'seconds');
let ranges: Array<IOptionDateRange> = [
  {
    id: 'today-only',
    label: 'Today Only',
    value: 0,
    start: moment(start).toDate(),
    end: moment(end).toDate()
  },
  {
    id: 'one-week',
    label: '1 Week',
    value: 1,
    start: moment(start).subtract(7, 'days').toDate(),
    end: moment(end).toDate()
  },
  {
    id: 'two-week',
    label: '2 Week',
    value: 2,
    start: moment(start).subtract(14, 'days').toDate(),
    end: moment(end).toDate()
  },
  {
    id: 'one-month',
    label: '1 Month',
    value: 3,
    start: moment(start).subtract(1, 'months').toDate(),
    end: moment(end).toDate()
  },
  {
    id: 'custom',
    label: 'Custom',
    value: 4
  }
];

const SelectDateRangeRetail = ({
  selectProps,
  comparison = false
}: {
  selectProps: SelectProps;
  comparison?: boolean | 'onm' | 'ofm';
}) => {
  const dateRangeOptionActive = useAppSelector(
    (state) => state.analyticArguments.dateRangeOptionActive.value
  );
  const dateRange = useAppSelector((state) =>
    comparison
      ? (comparison === 'ofm'
          ? state.ofmComparison
          : comparison === 'onm'
          ? state.onmComparison
          : state.storeComparison
        ).dateRange
      : state.analyticArguments.dateRange
  );
  const updateComparisonDateRange =
    comparison === 'ofm'
      ? reducerUpdateOfmComparisonDateRange
      : comparison === 'onm'
      ? reducerUpdateOnmComparisonDateRange
      : reducerUpdateComparisonDateRange;
  const [selectedRange, setSelectedRange] = useState<string | number>(() => {
    if (!comparison) return dateRangeOptionActive;
    return (
      ranges.find(
        ({ value, start, end }) =>
          value !== 4 &&
          moment(dateRange.start).isSame(start) &&
          moment(dateRange.end).isSame(end)
      )?.value ?? 4
    );
  });

  const dispatch = useAppDispatch();
  const { subscriptionEndDate } = useAppSelector(
    (state) => state.users.project
  );
  const userType = useAppSelector((state) => state.users.project.customerType);
  const { handleClickAlert } = useAlert();

  // terminated day
  const periodEnd = !!subscriptionEndDate
    ? moment(subscriptionEndDate).add(14, 'days').toDate()
    : null;

  // timeframe option
  const handleChangeTimeframeOption = (
    event: SelectChangeEvent<number>,
    child: React.ReactNode
  ) => {
    const value = Number(event.target.value);
    const targetOption = ranges.find((item) => item.value === value) || ranges[1];
    const payloadDateRange = {
      start: targetOption.start,
      end: targetOption.end
    };

    const isDateRangeOutsideSubscriptionPeriod =
      moment(payloadDateRange.start).isAfter(periodEnd) ||
      moment(payloadDateRange.end).isAfter(periodEnd);

    const isCustomOption = value === 4;

    if (
      !isCustomOption &&
      userType === 'b2c' &&
      isDateRangeOutsideSubscriptionPeriod
    ) {
      handleClickAlert({
        message: 'Access Denied: Outside of Billing Period',
        severity: 'error',
        horizontal: 'center',
        vertical: 'top'
      });
      return;
    }

    setSelectedRange(value);
    // for custom timeframe
    if (value === 4) return;

    const newGranularity = getGranularityForChronological(payloadDateRange);

    if (!comparison) {
      dispatch(reducerUpdateAnalyticArgumentDateRangeOptions(targetOption));
    }
    if (comparison) {
      dispatch(updateComparisonDateRange(payloadDateRange));
    } else {
      dispatch(reducerUpdateAnalyticArgumentDateRange(payloadDateRange));
      dispatch(reducerUpdateStatisticGranularity(newGranularity));
    }
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeCustomTimeframe = (dateRange: IDateRange) => {
    const currentOption = ranges.find((item) => item.value === Number(selectedRange)) || ranges[4];
    if (!comparison) {
      dispatch(
        reducerUpdateAnalyticArgumentDateRangeOptions({
          ...currentOption,
          start: moment(dateRange.start).toDate(),
          end: moment(dateRange.end).toDate()
        })
      );
    }
    const newGranularity = getGranularityForChronological(dateRange);

    if (comparison) {
      dispatch(updateComparisonDateRange(dateRange));
    } else {
      dispatch(reducerUpdateAnalyticArgumentDateRange(dateRange));
      dispatch(reducerUpdateStatisticGranularity(newGranularity));
    }
    setOpen(false);
  };

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep10 =
    stepNumber === 10 && isOnTour && menu === 'customization';

  return (
    <>
      <ProductTourTooltip
        placement="right"
        maxWidth={200}
        open={isCurrentlyOnTourStep10 && !comparison}
      >
        <Select
          id={comparison ? 'comparisonDateRange' : 'dateRange'}
          name={comparison ? 'comparisonDateRange' : 'dateRange'}
          value={selectedRange}
          onChange={!isOnTour ? handleChangeTimeframeOption : undefined}
          // disabled={loading}
          {...selectProps}
          sx={{
            ...selectProps.sx,
            ...(isCurrentlyOnTourStep10
              ? {
                position: 'relative',
                zIndex: open ? 100 : 9999
              }
              : {})
          }}
        >
          {ranges.map(({ id, value, label }) => (
            <MenuItem
              key={id}
              id={id}
              value={value}
              onClick={() => {
                if (value !== 4) return;
                handleClickOpen();
              }}
            >
              {label ? t(label.toLowerCase()) : ''}
            </MenuItem>
          ))}
        </Select>
      </ProductTourTooltip>
      <ModalCustomTimeFrame
        open={open}
        onClose={handleClose}
        onSet={!isOnTour ? handleChangeCustomTimeframe : undefined}
        maxDate={periodEnd}
        initDateRange={dateRange}
      />
    </>
  );
};

export default SelectDateRangeRetail;
